import { useState } from "react";
import { motion } from "framer-motion";
import { Check, BookOpen, FileText, HelpCircle, BarChart3, Compass, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEMS = [
  { icon: FileText, title: "Property Profile", desc: "Detailed report on the estate property: ownership, liens, comparables." },
  { icon: BookOpen, title: "Probate E-Book", desc: "A plain-English guide to the probate journey from first filing to final sale." },
  { icon: HelpCircle, title: "Frequently Asked Questions", desc: "Answers to the questions families ask us most — without the legalese." },
  { icon: BarChart3, title: "Home Evaluation PCMA", desc: "A Probate-specific Comparative Market Analysis tailored to estate sales." },
  { icon: Compass, title: "Probate Guidance", desc: "Personal walkthrough with a probate-certified specialist. No obligation." },
];

const EBOOK_IMG =
  "https://static.prod-images.emergentagent.com/jobs/888d0eda-de4f-401a-8d40-07873428fc54/images/e91803ed304034759f32cc6671a74e8e5aa83a6b14cf7962e52dc656bfdf3b66.png";

export default function FreePackage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_address: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/free-package`, form);
      toast.success("Your free package is on its way!", { description: "We'll send it to your email shortly." });
      setForm({ name: "", email: "", phone: "", property_address: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="free-package" data-testid="free-package-section" className="relative py-24 sm:py-32 bg-navy-50/40 overflow-hidden">
      <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-gold-500/10 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-navy-300/20 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: image + checklist */}
          <div className="lg:col-span-7">
            <span className="section-overline">Complimentary Resource</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight tracking-tight">
              Your <em className="italic text-gold-600 font-medium">Free</em> Probate Package
            </h2>
            <p className="mt-4 text-navy-700/80 max-w-xl">
              Everything a family needs to understand probate, evaluate the
              estate property, and move forward with clarity — beautifully
              organized in one delivery.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {/* E-book visual */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-2xl overflow-hidden bg-navy-900 aspect-[3/4]"
              >
                <img src={EBOOK_IMG} alt="Probate E-book" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold-400 mb-1">2026 Edition</div>
                  <div className="font-heading text-lg">The Probate Companion</div>
                </div>
              </motion.div>

              {/* Checklist */}
              <div className="space-y-3">
                {ITEMS.map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="group flex items-start gap-3 rounded-xl bg-white p-4 border border-navy-100 hover:border-gold-400 hover:shadow-md transition-all"
                    data-testid={`package-item-${i}`}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold-500/10 text-gold-600 group-hover:bg-gold-500 group-hover:text-white transition-colors">
                      <item.icon size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-semibold text-navy-900">{item.title}</span>
                        <Check size={14} className="text-gold-600" />
                      </div>
                      <p className="text-xs text-navy-600 mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="glass rounded-3xl p-8 sm:p-10 shadow-xl"
            >
              <div className="text-xs uppercase tracking-[0.25em] text-gold-700 font-semibold">Request It Now</div>
              <h3 className="mt-2 font-heading text-2xl sm:text-3xl text-navy-900">
                We'll send your package within 24 hours.
              </h3>
              <p className="mt-2 text-sm text-navy-600">No obligation. No spam. Just clarity.</p>

              <form className="mt-6 space-y-4" onSubmit={submit} data-testid="free-package-form">
                <div>
                  <label className="text-xs font-medium text-navy-700">Full name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="package-name"
                    className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-navy-700">Email</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="package-email"
                      className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                      placeholder="jane@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-navy-700">Phone</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="package-phone"
                      className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                      placeholder="(714) 391-3645"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy-700">Property address (optional)</label>
                  <input
                    value={form.property_address}
                    onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                    data-testid="package-address"
                    className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                    placeholder="2134 Main St, Huntington Beach, CA"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="package-submit"
                  className="w-full btn-gold disabled:opacity-60 group"
                >
                  {submitting ? "Sending..." : "Send My Free Package"}
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
